<script>
	import { base } from '$app/paths';
	import SeoHead from '../../../common/SeoHead.svelte';
	import guides from './guides.json';
	const origin = `https://tools.d-cine.net${base}`;
	const structuredData = JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'CollectionPage',
		name: 'Slate Tools Walk Throughs/Help',
		url: `${origin}/slate/help/`,
		description:
			'Illustrated cinema programming manuals for Movie DB, Release Timeline and Booking App, plus the current status of Release Analysis.',
		mainEntity: {
			'@type': 'ItemList',
			itemListElement: guides.map((guide, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				name: guide.heading,
				url: `${origin}/slate/help/${guide.slug}/`
			}))
		}
	}).replace(/</g, '\\u003c');
</script>

<SeoHead
	title="Slate Tools walkthroughs and help"
	description="Illustrated cinema programming manuals: research films in Movie DB, compare regional release dates and prepare facility bookings and distributor offers."
	path="/slate/help/"
	image="/slate-help/release-timeline-navigate.webp"
/>
<svelte:head>{@html `<script type="application/ld+json">${structuredData}</script>`}</svelte:head>

<main class="help-content">
	<header class="guide-header">
		<nav class="breadcrumbs" aria-label="Breadcrumb">
			<a href={`${base}/slate/welcome/`}>Slate Tools</a><span aria-hidden="true">/</span><span
				>Walk Throughs/Help</span
			>
		</nav>
		<p class="guide-kicker">Slate Tools user manuals</p>
		<h1>Walk Throughs/Help</h1>
		<p class="guide-lead">
			Research a film, compare its release window and prepare a distributor offer. Follow the
			illustrated guides through the tools available today.
		</p>
	</header>
	<section aria-labelledby="choose-guide">
		<h2 id="choose-guide">Choose your task</h2>
		<div class="guide-index">
			{#each guides as guide, index}
				<article>
					<a
						class="guide-preview"
						href={`${base}/slate/help/${guide.slug}/`}
						aria-label={`Read ${guide.title} guide`}
					>
						<img
							src={`${base}/slate-help/${guide.slug}-${guide.steps[0].id}.webp`}
							alt={`${guide.steps[0].visual.title}: labelled example of the controls or availability covered by this guide`}
							width="1200"
							height="800"
							loading={index < 2 ? 'eager' : 'lazy'}
						/>
					</a>
					<div class="guide-card-copy">
						<p class="guide-meta">
							{guide.status
								? 'Planned tool · availability overview'
								: `${guide.steps.length} illustrated steps · ${guide.time}`}
						</p>
						<h3><a href={`${base}/slate/help/${guide.slug}/`}>{guide.title}</a></h3>
						<p>{guide.intro}</p>
						<a class="guide-link" href={`${base}/slate/help/${guide.slug}/`}
							>{guide.status ? 'Read the current status' : 'Read the walkthrough'}
							<i class="bx bx-right-arrow-alt" aria-hidden="true"></i></a
						>
					</div>
				</article>
			{/each}
		</div>
	</section>
	<section class="help-band" aria-labelledby="planning-workflow">
		<h2 id="planning-workflow">From release research to an offer</h2>
		<ol class="workflow-path">
			<li>
				<strong>Research the film</strong>
				<p>Check its identity, regional dates, classifications, scores and available artwork.</p>
				<a href={`${base}/slate/help/movie-db/`}>Movie DB guide</a>
			</li>
			<li>
				<strong>Choose the opening window</strong>
				<p>
					Select a release region and period, narrow the slate and create facility booking entries.
				</p>
				<a href={`${base}/slate/help/release-timeline/`}>Release Timeline guide</a>
			</li>
			<li>
				<strong>Prepare the offer</strong>
				<p>
					Review each cinema's dates and terms, preview the email and follow the negotiation
					history.
				</p>
				<a href={`${base}/slate/help/booking-app/`}>Booking App guide</a>
			</li>
		</ol>
	</section>
	<section class="help-band" aria-labelledby="terms">
		<h2 id="terms">Important distinctions</h2>
		<dl class="help-glossary">
			<div>
				<dt>Release region</dt>
				<dd>
					The territory whose release dates you are viewing. It does not convert the AU distributor
					or AU classification into that region's metadata.
				</dd>
			</div>
			<div>
				<dt>Classification and score</dt>
				<dd>
					A classification describes content suitability in a region. A source score measures a
					provider's audience or critic response; it is not a classification.
				</dd>
			</div>
			<div>
				<dt>TN and NZFC</dt>
				<dd>
					Short source labels for The Numbers and the New Zealand release source. Compare source and
					release type as well as the date.
				</dd>
			</div>
			<div>
				<dt>MG, policy and terms</dt>
				<dd>
					Minimum guarantee, the recorded commitment policy and the proposed commercial terms for
					each facility. Enter the actual proposal, not the guide's sample values.
				</dd>
			</div>
			<div>
				<dt>Offer and acceptance</dt>
				<dd>
					A sent offer records a proposal being sent. It does not itself prove agreement, film
					delivery or readiness for playback.
				</dd>
			</div>
			<div>
				<dt>Slate booking and KDM booking</dt>
				<dd>
					Slate Tools manages film programming and offers. KdmGen separately manages key-generation
					requests and the bookings that follow device changes.
				</dd>
			</div>
		</dl>
	</section>
	<aside class="help-support">
		<strong>Need assistance?</strong>
		<p>
			Email <a href="mailto:support@d-cine.net">support@d-cine.net</a> with the tool, selected film or
			booking identity, and the exact error. Avoid posting recipient addresses or commercial terms in
			public bug reports. Real screenshots are labelled separately from the fictional step examples.
		</p>
	</aside>
</main>
